import React, { useState, useEffect, useRef } from "react"; // <-- useRef importiert
import StandPlan from "./StandPlan";
import { useLanguage } from "./LanguageContext";
import QRCode from "react-qr-code";
import { createStandplanPDF } from "./createStandplanPDF.js";

export default function ExportView({
  highlightStands = [],
  selectedCategories = [],
  searchText = "",
  requireBoth = false
}) {
  const { lang } = useLanguage();
  const [isExporting, setIsExporting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isKiosk, setIsKiosk] = useState(false);

  const isHighlightSelected = Array.isArray(highlightStands) && highlightStands.length > 0;
  const isCategorySelected = Array.isArray(selectedCategories) && selectedCategories.length > 0;
  const isSearchActive = searchText && searchText.trim() !== "";

  const activeItemsCount = [isHighlightSelected, isCategorySelected, isSearchActive].filter(Boolean).length;
  const canExportPDF = requireBoth ? activeItemsCount === 3 : activeItemsCount > 0;

  useEffect(() => {
    setIsKiosk(
      window.innerHeight === window.screen.height &&
      window.innerWidth === window.screen.width
    );
  }, []);

  // PDF erzeugen
  const handleExportPDF = async () => {
    if (!canExportPDF) return;
    setIsExporting(true);

    try {
      const url = await createStandplanPDF({
        highlightStands,
        selectedCategories,
        lang
      });
      if (url) {
        setPdfUrl(url);
        setShowModal(true);
      }
    } catch (err) {
      console.error("PDF-Export fehlgeschlagen", err);
    } finally {
      setIsExporting(false);
    }
  };

  // QR-Share benutzt dieselbe Funktion
  const handleShare = handleExportPDF;

  const iframeRef = useRef(null);

  useEffect(() => {
    if (showModal && iframeRef.current) {
      iframeRef.current.contentWindow?.scrollTo(0, 0);
    }
  }, [showModal, pdfUrl]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize(); // initial prüfen
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const emailBody = `Hier ist der Standplan: https://biofach2026.itaterminal.com${pdfUrl || ""}`;
  return (
    <div className="mt-4 relative">
      {isExporting && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg flex flex-col items-center shadow-lg">
            <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin mb-4" />
            <span className="font-semibold">{lang === "de" ? "PDF wird erzeugt…" : "Generating PDF…"}</span>
          </div>
        </div>
      )}

      {!isExporting && (
        <div className="flex justify-center gap-3 flex-wrap mb-4">
          <button
            onClick={handleShare}
            disabled={!canExportPDF || isExporting}
            className={`py-2 px-3 rounded-lg text-sm text-white transition ${
              !canExportPDF || isExporting
                ? "bg-green-500 opacity-50 cursor-not-allowed"
                : "bg-green-500 hover:bg-green-600"
            }`}
          >
            QR-Codes erzeugen
          </button>

          <button
            onClick={handleExportPDF}
            disabled={!canExportPDF || isExporting}
            className={`py-2 px-4 rounded-lg text-sm text-white transition ${
              !canExportPDF || isExporting
                ? "bg-green-600 opacity-50 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {lang === "de" ? "PDF anzeigen" : "Show PDF"}
          </button>
        </div>
      )}


      {/* Vorschau des Standplans */}
      <div className="border rounded-2xl overflow-hidden p-4 bg-white mb-4">
        <StandPlan 
          highlightStands={highlightStands} 
          selectedCategories={selectedCategories} 
          printable={false} // interaktive Vorschau
        />
      </div>

      {/* QR-Code Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-auto">
          <div className="bg-white w-11/12 max-w-4xl h-auto rounded-lg shadow-lg relative flex flex-col p-6">
            <button
              onClick={() => { setShowModal(false); setPdfUrl(null); }}
              className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 transition text-sm z-10"
            >
              {lang === "de" ? "Schließen" : "Close"}
            </button>

           <div className="flex flex-col md:flex-row gap-6 mt-8">
              {/* PDF-iframe: immer sichtbar */}
              <div className="flex-1 overflow-auto max-h-[80vh]">
                <iframe
                  src={pdfUrl ? `${pdfUrl}#page=1` : ""}
                  title="PDF Vorschau"
                  className="w-full h-[80vh] md:h-full rounded-lg"
                />
              </div>

              {/* QR-Codes: nur Desktop (ab md) */}
              {!isMobile && (
                <div className="flex flex-col gap-6 justify-start mt-4 md:mt-0 md:ml-4 items-center">
                  
                  {/* Logo + Titel */}
                  <div className="flex flex-col items-center">
                    <img
                      src="/logo192.png" // <-- dein Seitenlogo hier
                      alt="ITA auf der Biofach 2026"
                      className="w-24 h-auto mb-2"
                    />
                    <h2 className="text-xl font-bold mb-1">
                      {lang === "de" ? "Standplan PDF" : "Stand Plan PDF"}
                    </h2>
                    {pdfUrl && (
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:underline break-all text-center"
                      >
                        {pdfUrl}
                      </a>
                    )}
                  </div>

                  {/* QR-Codes */}
                  <div className="flex flex-col gap-6 justify-start mt-4 md:mt-0 md:ml-0 items-center">
                    {/* WhatsApp */}
                    <div className="flex flex-col items-center">
                      <QRCode 
                        value={`https://wa.me/?text=${encodeURIComponent(`Hier ist der Standplan: https://biofach2026.itaterminal.com/preview.html?file=${pdfUrl || ""}`)}`} 
                        size={128} 
                      />
                      {lang === "de" ? "WhatsApp öffnen" : "WhatsApp open"}
                    </div>

                    {/* Facebook 
                    <div className="flex flex-col items-center">
                      <QRCode 
                        value={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pdfUrl || "")}`} 
                        size={128} 
                      />
                      {lang === "de" ? "Facebook teilen" : "Facebook share"}
                    </div>*/}

                    {/* E-Mail */}
                    <div className="flex flex-col items-center">
                      <QRCode 
                        value={`mailto:?subject=Standplan&body=${emailBody}`} 
                        size={128} 
                      />
                      {lang === "de" ? "E-Mail öffnen" : "Email open"}
                    </div>

                    {/* SMS 
                    <div className="flex flex-col items-center">
                      <QRCode 
                        value={`sms:?body=${encodeURIComponent(`Hier ist der Standplan: ${pdfUrl || ""}`)}`} 
                        size={128} 
                      />
                      {lang === "de" ? "SMS öffnen" : "SMS open"}
                    </div>*/}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
